import React, { useEffect, useMemo, useState } from 'react';
import { Shield, KeyRound, Package2, Mail, Siren, Database } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { workspaceAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { StatCard } from '../common/StatCard';
import { CardSkeleton } from '../common/States';

const joinList = (value) => (Array.isArray(value) ? value.join(', ') : '');
const parseList = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const sectionMeta = [
  { key: 'policy_guardrails', title: 'Tenant Policy Guardrails', icon: Shield },
  { key: 'identity_federation', title: 'Identity Federation', icon: KeyRound },
  { key: 'subscription_feature_access', title: 'Subscription & Feature Access', icon: Package2 },
  { key: 'cross_tenant_communication', title: 'Cross-Tenant Communication', icon: Mail },
  { key: 'platform_audit_user_risk', title: 'Platform Audit & User Risk', icon: Siren },
  { key: 'global_data_retention_minimums', title: 'Global Data Retention', icon: Database },
];

const PlatformGovernancePanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await workspaceAdminAPI.getPlatformGovernanceProfile();
      setForm(response?.data?.config_value || {});
      setVersion(response?.data?.version || 0);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load platform governance profile'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const adminMfaRequired = Boolean(
    form?.identity_federation?.mfa_enforcement_baselines?.require_mfa_for_admins
  );
  // Arming this while the operator has no authenticator means their next
  // sign-in is an enrolment, not a password. Worth saying before they save,
  // not after.
  const mfaArmingWithoutEnrolment = adminMfaRequired && !user?.mfaEnrolled;

  const handleAdminMfaToggle = (checked) => {
    if (checked && !user?.mfaEnrolled) {
      const proceed = window.confirm(
        'Require MFA for admins?\n\n'
        + 'This covers your own superadmin account, and you have no authenticator '
        + 'enrolled. At your next sign-in you will be asked to enrol one before you '
        + 'can get in.\n\nContinue?'
      );
      if (!proceed) return;
    }
    updateNestedValue('identity_federation', 'mfa_enforcement_baselines', 'require_mfa_for_admins', checked);
  };

  const updateNestedValue = (section, group, key, value) => {
    setForm((prev) => ({
      ...(prev || {}),
      [section]: {
        ...(prev?.[section] || {}),
        [group]: {
          ...(prev?.[section]?.[group] || {}),
          [key]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await workspaceAdminAPI.updatePlatformGovernanceProfile({ config_value: form || {} });
      setForm(response?.data?.config_value || {});
      setVersion(response?.data?.version || 0);
      toast.success('Platform governance profile updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update platform governance profile'));
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    if (!form) return [];
    return [
      {
        label: 'Admin MFA',
        value: form.identity_federation?.mfa_enforcement_baselines?.require_mfa_for_admins ? 'Required' : 'Optional',
        icon: KeyRound,
        tone: 'primary',
      },
      {
        label: 'Default User Limit',
        value: form.subscription_feature_access?.plan_based_user_limits?.default_limit ?? 0,
        icon: Package2,
        tone: 'info',
      },
      {
        label: 'Bulk Export Approval',
        value: form.global_data_retention_minimums?.export_restrictions?.require_superadmin_approval_for_bulk_export ? 'Required' : 'Optional',
        icon: Database,
        tone: 'warning',
      },
      {
        label: 'Email Rate Limit',
        value: form.cross_tenant_communication?.outbound_rate_protections?.email_rate_limit_per_minute ?? 0,
        icon: Mail,
        tone: 'success',
      },
    ];
  }, [form]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} rows={2} />
          ))}
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} rows={5} />
          ))}
        </div>
      </div>
    );
  }

  if (!form) {
    return <div className="text-sm text-muted-foreground">Platform governance profile unavailable.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-xl font-semibold">Platform Governance Baselines</h3>
          <p className="text-sm text-muted-foreground">
            Centralized guardrails owned by the superadmin control plane. Version {version || 0}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProfile}>
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Governance'}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {summary.map((item) => (
          <StatCard key={item.label} label={item.label} value={item.value} icon={item.icon} tone={item.tone} />
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Shield className="h-[18px] w-[18px]" />
              </span>
              Tenant Policy Guardrails
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Default Password Minimum</Label>
                <Input
                  type="number"
                  value={form.policy_guardrails?.global_defaults?.default_password_min_length ?? 12}
                  onChange={(e) => updateNestedValue('policy_guardrails', 'global_defaults', 'default_password_min_length', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Password Rotation Days</Label>
                <Input
                  type="number"
                  value={form.policy_guardrails?.non_overridable_security_baselines?.minimum_password_rotation_days ?? 90}
                  onChange={(e) => updateNestedValue('policy_guardrails', 'non_overridable_security_baselines', 'minimum_password_rotation_days', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Audit Trail Required</Label>
                <p className="text-xs text-muted-foreground">Prevents tenant override of platform evidence requirements.</p>
              </div>
              <Switch
                checked={Boolean(form.policy_guardrails?.mandatory_compliance_controls?.require_audit_trail)}
                onCheckedChange={(checked) => updateNestedValue('policy_guardrails', 'mandatory_compliance_controls', 'require_audit_trail', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Custom Modules Allowed</Label>
                <p className="text-xs text-muted-foreground">Limits tenant autonomy over risky feature surfaces.</p>
              </div>
              <Switch
                checked={Boolean(form.policy_guardrails?.feature_entitlements?.allow_custom_modules)}
                onCheckedChange={(checked) => updateNestedValue('policy_guardrails', 'feature_entitlements', 'allow_custom_modules', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <KeyRound className="h-[18px] w-[18px]" />
              </span>
              Identity Federation Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Allowed SSO Protocols</Label>
              <Input
                value={joinList(form.identity_federation?.sso_policy_templates?.allowed_protocols)}
                onChange={(e) => updateNestedValue('identity_federation', 'sso_policy_templates', 'allowed_protocols', parseList(e.target.value))}
                placeholder="saml, oidc"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Session Timeout (Minutes)</Label>
                <Input
                  type="number"
                  value={form.identity_federation?.session_security_minimums?.session_timeout_minutes ?? 60}
                  onChange={(e) => updateNestedValue('identity_federation', 'session_security_minimums', 'session_timeout_minutes', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Concurrent Session Limit</Label>
                <Input
                  type="number"
                  value={form.identity_federation?.session_security_minimums?.concurrent_session_limit ?? 5}
                  onChange={(e) => updateNestedValue('identity_federation', 'session_security_minimums', 'concurrent_session_limit', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Require Domain Verification</Label>
                <p className="text-xs text-muted-foreground">Only verified tenant domains can be bound to SSO.</p>
              </div>
              <Switch
                checked={Boolean(form.identity_federation?.domain_verification_rules?.require_dns_proof)}
                onCheckedChange={(checked) => updateNestedValue('identity_federation', 'domain_verification_rules', 'require_dns_proof', checked)}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label>Require MFA For Admins</Label>
                {/* The old copy said "tenant admins", which read as somebody
                    else. ADMIN_ROLE_KEYS in governance_enforcement.py is
                    {superadmin, workspace_admin, hr_admin} — arming this puts
                    the operator reading this screen behind MFA at their own
                    next sign-in, so the label has to say so. */}
                <p className="text-xs text-muted-foreground">
                  Superadmin, workspace admin and HR admin — including your own account.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Your account:{' '}
                  <span className="font-medium text-foreground">
                    {user?.mfaRequired ? 'MFA required' : 'MFA not required'}
                  </span>
                  {' · '}
                  <span className={user?.mfaEnrolled ? 'font-medium text-foreground' : 'font-medium text-warning'}>
                    {user?.mfaEnrolled ? 'authenticator enrolled' : 'no authenticator enrolled'}
                  </span>
                </p>
                {mfaArmingWithoutEnrolment && (
                  <p className="mt-1 text-xs text-warning">
                    You will be asked to enrol an authenticator app the next time you sign in.
                  </p>
                )}
              </div>
              <Switch
                checked={Boolean(form.identity_federation?.mfa_enforcement_baselines?.require_mfa_for_admins)}
                onCheckedChange={handleAdminMfaToggle}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Package2 className="h-[18px] w-[18px]" />
              </span>
              Subscription & Feature Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Default User Limit</Label>
                <Input
                  type="number"
                  value={form.subscription_feature_access?.plan_based_user_limits?.default_limit ?? 100}
                  onChange={(e) => updateNestedValue('subscription_feature_access', 'plan_based_user_limits', 'default_limit', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Storage Quota (GB)</Label>
                <Input
                  type="number"
                  value={form.subscription_feature_access?.storage_quotas?.default_quota_gb ?? 100}
                  onChange={(e) => updateNestedValue('subscription_feature_access', 'storage_quotas', 'default_quota_gb', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>API Limit / Minute</Label>
                <Input
                  type="number"
                  value={form.subscription_feature_access?.api_limits?.default_rate_limit_per_minute ?? 120}
                  onChange={(e) => updateNestedValue('subscription_feature_access', 'api_limits', 'default_rate_limit_per_minute', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Billing Grace Days</Label>
                <Input
                  type="number"
                  value={form.subscription_feature_access?.billing_usage_caps?.grace_period_days ?? 7}
                  onChange={(e) => updateNestedValue('subscription_feature_access', 'billing_usage_caps', 'grace_period_days', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Enforce Plan Module Gating</Label>
                <p className="text-xs text-muted-foreground">Tenants cannot self-enable modules outside commercial entitlements.</p>
              </div>
              <Switch
                checked={Boolean(form.subscription_feature_access?.module_entitlements?.enforce_plan_module_gating)}
                onCheckedChange={(checked) => updateNestedValue('subscription_feature_access', 'module_entitlements', 'enforce_plan_module_gating', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Mail className="h-[18px] w-[18px]" />
              </span>
              Cross-Tenant Communication Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Approved Sender Domains</Label>
              <Input
                value={joinList(form.cross_tenant_communication?.sender_reputation_rules?.approved_sender_domains)}
                onChange={(e) => updateNestedValue('cross_tenant_communication', 'sender_reputation_rules', 'approved_sender_domains', parseList(e.target.value))}
                placeholder="appable.com"
              />
            </div>
            <div>
              <Label>Restricted Template Keys</Label>
              <Textarea
                value={joinList(form.cross_tenant_communication?.restricted_templates?.blocked_template_keys)}
                onChange={(e) => updateNestedValue('cross_tenant_communication', 'restricted_templates', 'blocked_template_keys', parseList(e.target.value))}
                placeholder="password_reset_override, billing_collection_notice"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Email Rate Limit / Minute</Label>
                <Input
                  type="number"
                  value={form.cross_tenant_communication?.outbound_rate_protections?.email_rate_limit_per_minute ?? 120}
                  onChange={(e) => updateNestedValue('cross_tenant_communication', 'outbound_rate_protections', 'email_rate_limit_per_minute', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>SMS Rate Limit / Minute</Label>
                <Input
                  type="number"
                  value={form.cross_tenant_communication?.outbound_rate_protections?.sms_rate_limit_per_minute ?? 60}
                  onChange={(e) => updateNestedValue('cross_tenant_communication', 'outbound_rate_protections', 'sms_rate_limit_per_minute', Number(e.target.value || 0))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Siren className="h-[18px] w-[18px]" />
              </span>
              Platform Audit & User-Risk Oversight
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Failed Logins / 24h</Label>
                <Input
                  type="number"
                  value={form.platform_audit_user_risk?.suspicious_login_patterns?.failed_login_threshold_24h ?? 10}
                  onChange={(e) => updateNestedValue('platform_audit_user_risk', 'suspicious_login_patterns', 'failed_login_threshold_24h', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Bulk Exports / 24h</Label>
                <Input
                  type="number"
                  value={form.platform_audit_user_risk?.abusive_user_behavior_signals?.bulk_export_threshold_24h ?? 5}
                  onChange={(e) => updateNestedValue('platform_audit_user_risk', 'abusive_user_behavior_signals', 'bulk_export_threshold_24h', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Privilege Changes / 24h</Label>
                <Input
                  type="number"
                  value={form.platform_audit_user_risk?.abusive_user_behavior_signals?.privilege_change_threshold_24h ?? 3}
                  onChange={(e) => updateNestedValue('platform_audit_user_risk', 'abusive_user_behavior_signals', 'privilege_change_threshold_24h', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Review SLA (Hours)</Label>
                <Input
                  type="number"
                  value={form.platform_audit_user_risk?.escalated_audit_review?.review_sla_hours ?? 24}
                  onChange={(e) => updateNestedValue('platform_audit_user_risk', 'escalated_audit_review', 'review_sla_hours', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Impossible Travel Detection</Label>
                <p className="text-xs text-muted-foreground">Keeps cross-tenant suspicious login evidence centrally governed.</p>
              </div>
              <Switch
                checked={Boolean(form.platform_audit_user_risk?.suspicious_login_patterns?.impossible_travel_detection)}
                onCheckedChange={(checked) => updateNestedValue('platform_audit_user_risk', 'suspicious_login_patterns', 'impossible_travel_detection', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Database className="h-[18px] w-[18px]" />
              </span>
              Global Data Retention Minimums
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Employee Document Days</Label>
                <Input
                  type="number"
                  value={form.global_data_retention_minimums?.minimum_retention?.employee_document_days ?? 2555}
                  onChange={(e) => updateNestedValue('global_data_retention_minimums', 'minimum_retention', 'employee_document_days', Number(e.target.value || 0))}
                />
              </div>
              <div>
                <Label>Audit Event Days</Label>
                <Input
                  type="number"
                  value={form.global_data_retention_minimums?.minimum_retention?.audit_event_days ?? 3650}
                  onChange={(e) => updateNestedValue('global_data_retention_minimums', 'minimum_retention', 'audit_event_days', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Backup Retention Floor</Label>
                <Input
                  type="number"
                  value={form.global_data_retention_minimums?.backup_policy_floor?.minimum_backup_retention_days ?? 30}
                  onChange={(e) => updateNestedValue('global_data_retention_minimums', 'backup_policy_floor', 'minimum_backup_retention_days', Number(e.target.value || 0))}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Legal Hold Enabled</Label>
                  <p className="text-xs text-muted-foreground">Tenants cannot release platform-managed legal holds.</p>
                </div>
                <Switch
                  checked={Boolean(form.global_data_retention_minimums?.legal_hold?.enabled)}
                  onCheckedChange={(checked) => updateNestedValue('global_data_retention_minimums', 'legal_hold', 'enabled', checked)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Require Superadmin Approval For Bulk Export</Label>
                <p className="text-xs text-muted-foreground">Preserves centralized control over high-liability data exports.</p>
              </div>
              <Switch
                checked={Boolean(form.global_data_retention_minimums?.export_restrictions?.require_superadmin_approval_for_bulk_export)}
                onCheckedChange={(checked) => updateNestedValue('global_data_retention_minimums', 'export_restrictions', 'require_superadmin_approval_for_bulk_export', checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {sectionMeta.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.key} className="card-interactive border-primary/10 bg-primary/[0.03]">
              <CardContent className="pt-5">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Managed by {form[item.key]?.managed_by || 'superadmin'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformGovernancePanel;
